import './_posReceiptCard.scss'

import { useState } from 'react'

import { printerIco } from '@/assets/icons'
import { useTranslate } from '@/i18n/useTranslate'
import { printPosSlipTemplate } from '@/utilities/paymentHelper'
import { PrintSlipRequest } from '@/utilities/posSlipHelper'

export interface PosReceiptCardProps {
    slip: PrintSlipRequest
    sessionId?: string
}

type PrintState = 'idle' | 'printing' | 'printed' | 'error'

export default function PosReceiptCard({
    slip,
    sessionId
}: PosReceiptCardProps) {
    const { t } = useTranslate()
    const [printState, setPrintState] = useState<PrintState>('idle')

    const handlePrint = async () => {
        if (printState === 'printing') {
            return
        }
        setPrintState('printing')
        const result = await printPosSlipTemplate(slip, () => {}, sessionId)
        setPrintState(result.success ? 'printed' : 'error')
    }

    const isApproved =
        slip.transactionStatus?.toLowerCase() === 'approved'
    const statusLabel = isApproved
        ? t('cardPayment.posReceiptApproved')
        : t('cardPayment.posReceiptDeclined')
    const slipDate =
        slip.transactionDate && slip.transactionTime
            ? `${slip.transactionDate} ${slip.transactionTime}`
            : (slip.transactionDate ?? slip.transactionTime)

    const printBtnLabel =
        printState === 'printing'
            ? t('posSlip.printing')
            : printState === 'printed'
              ? t('posSlip.printed')
              : t('cardPayment.posReceiptPrintBtn')

    return (
        <div className="pos-receipt-card">
            <div className="pos-receipt-card__slip">
                <div className="pos-receipt-card__status-row">
                    <span className="pos-receipt-card__status-label">
                        {t('cardPayment.posReceiptStatus')}
                    </span>
                    <span
                        className={`pos-receipt-card__status-value${isApproved ? ' pos-receipt-card__status-value--approved' : ''}`}>
                        {statusLabel}
                    </span>
                </div>

                {slip.terminalId && (
                    <div className="pos-receipt-card__row">
                        <span className="pos-receipt-card__row-label">
                            {t('cardPayment.posReceiptTerminalId')}
                        </span>
                        <span className="pos-receipt-card__row-value">
                            #{slip.terminalId}
                        </span>
                    </div>
                )}
                {slipDate && (
                    <div className="pos-receipt-card__row">
                        <span className="pos-receipt-card__row-label">
                            {t('cardPayment.posReceiptDate')}
                        </span>
                        <span className="pos-receipt-card__row-value">
                            {slipDate}
                        </span>
                    </div>
                )}
                {slip.cardNumber && (
                    <div className="pos-receipt-card__row">
                        <span className="pos-receipt-card__row-label">
                            {t('cardPayment.posReceiptCardNumber')}
                        </span>
                        <span className="pos-receipt-card__row-value">
                            {slip.cardNumber}
                        </span>
                    </div>
                )}
                {(slip.cardApplicationName || slip.cardIssuer) && (
                    <div className="pos-receipt-card__row">
                        <span className="pos-receipt-card__row-label">
                            {t('cardPayment.posReceiptCardType')}
                        </span>
                        <span className="pos-receipt-card__row-value">
                            {slip.cardApplicationName ?? slip.cardIssuer}
                        </span>
                    </div>
                )}
                {slip.merchantId && (
                    <div className="pos-receipt-card__row">
                        <span className="pos-receipt-card__row-label">
                            {t('cardPayment.posReceiptMid')}
                        </span>
                        <span className="pos-receipt-card__row-value">
                            {slip.merchantId}
                        </span>
                    </div>
                )}
                {slip.invoiceNumber && (
                    <p className="pos-receipt-card__invoice-number">
                        BROJ RAČUNA: {slip.invoiceNumber}
                    </p>
                )}
            </div>

            <button
                type="button"
                className="pos-receipt-card__print-btn"
                onClick={handlePrint}
                disabled={printState === 'printing'}>
                <img src={printerIco} alt="" aria-hidden="true" />
                {printBtnLabel}
            </button>

            {printState === 'error' && (
                <p className="pos-receipt-card__print-error">
                    {t('posSlip.printError')}
                </p>
            )}
        </div>
    )
}
